import { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { FiZap, FiUser, FiSend, FiCopy, FiTrash2 } from "react-icons/fi";
import { RiRobot2Line } from "react-icons/ri";
import { BsCheckCircle } from "react-icons/bs";
import "./Suggestion.css";

// Backend API URL
const API_BASE_URL = process.env.REACT_APP_BACKENDD_URL;

const suggestions = [
  "What is PHP and its uses?",
  "Explain Python list comprehensions",
  "How to build a React component?",
  "What's the difference between var, let, and const?",
  "Explain async/await in JavaScript",
  "How to optimize website performance?",
  "What are RESTful APIs?",
  "Explain machine learning basics"
];

function Suggestion() {
  const [chatHistory, setChatHistory] = useState([
    {
      type: "ai",
      content:
        "👋 Hello! I'm your AI coding assistant. Ask me anything about programming, web development, or technology!\n\n💡 **Try clicking on a suggestion below** or type your own question."
    }
  ]);
  const [question, setQuestion] = useState("");
  const [generatingAnswer, setGeneratingAnswer] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState("");

  useEffect(() => {
    checkBackendConnection();
  }, []);

  // Enhanced scroll handling
  useEffect(() => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      
      if (isAtBottom || generatingAnswer || aiStreaming) {
        scrollToBottom();
      }
    }
  }, [chatHistory, generatingAnswer, aiStreaming, streamedContent]);

  // Smooth scroll to bottom
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Manual scroll to bottom button
  const handleScrollToBottom = () => {
    scrollToBottom();
  };

  // Backend health check
  const checkBackendConnection = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/ai-suggest/health`);
      setIsConnected(response.data.status === "healthy");
    } catch {
      setIsConnected(false);
      setChatHistory((prev) => [
        ...prev,
        {
          type: "ai",
          content:
            "⚠️ Our AI service is temporarily unavailable. Please try again in a few minutes.",
          isError: true,
          id: Date.now()
        }
      ]);
    }
  };

  // Simulate streaming effect for AI response
  const simulateStreaming = (text, speed = 20) => {
    return new Promise((resolve) => {
      let index = 0;
      setStreamedContent("");
      
      const interval = setInterval(() => {
        if (index < text.length) {
          setStreamedContent(prev => prev + text.charAt(index));
          index++;
          
          // Scroll during streaming
          if (index % 10 === 0) {
            scrollToBottom();
          }
        } else {
          clearInterval(interval);
          resolve();
        }
      }, speed);
    });
  };

  // Generate AI answer with streaming effect
  const generateAnswer = async (e, predefinedQuestion) => {
    if (e) e.preventDefault();
    const finalQuestion = predefinedQuestion || question;
    if (!finalQuestion.trim()) return;

    // Add user message
    const userMessageId = Date.now();
    setChatHistory((prev) => [
      ...prev,
      { 
        type: "user", 
        content: finalQuestion, 
        timestamp: new Date().toLocaleTimeString(),
        id: userMessageId
      }
    ]);
    setQuestion("");
    setGeneratingAnswer(true);
    setAiStreaming(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/ai-suggest`,
        { question: finalQuestion },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        const aiMessageId = Date.now() + 1;
        const answer = response.data.answer;
        
        // Add placeholder AI message
        setChatHistory((prev) => [
          ...prev,
          {
            type: "ai",
            content: "",
            timestamp: new Date().toLocaleTimeString(),
            id: aiMessageId,
            isStreaming: true
          }
        ]);

        // Scroll to show the new AI message
        setTimeout(() => scrollToBottom(), 100);

        // Simulate streaming effect
        await simulateStreaming(answer, 15);

        // Update with complete message
        setChatHistory((prev) => 
          prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: answer, isStreaming: false }
              : msg
          )
        );
      } else {
        throw new Error("API response unsuccessful");
      }
    } catch (error) {
      const errorMessageId = Date.now() + 1;
      setChatHistory((prev) => [
        ...prev,
        {
          type: "ai",
          content: "⚠️ Unable to get AI response at the moment. Please try again later.",
          timestamp: new Date().toLocaleTimeString(),
          isError: true,
          id: errorMessageId
        }
      ]);
    } finally {
      setGeneratingAnswer(false);
      setAiStreaming(false);
      setStreamedContent("");
    }
  };

  // Clear chat
  const clearChat = () => {
    setChatHistory([
      {
        type: "ai",
        content:
          "Chat cleared! Ask me anything about programming, web development, or technology! 💻",
        id: Date.now()
      }
    ]);
  };

  // Copy to clipboard with feedback
  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="ai-suggestion-page">
      {/* Background Effects */}
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="page-wrapper">
        {/* Header */}
        <header className="header">
          <div className="logo-container max-w-6xl mx-auto px-6 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="avatar avatar-ai pulse">
                  <RiRobot2Line size={24} />
                </div>
                <div>
                  <h1 className="title">V-EDITOR AI Assistant</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className={`connection-dot ${
                        isConnected ? "connected" : "disconnected"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {isConnected ? "Connected" : "Disconnected"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={clearChat}
                  className="clear-chat-btn"
                  disabled={chatHistory.length <= 1 || generatingAnswer}
                >
                  <FiTrash2 />
                  Clear Chat
                </button>
                <div className="message-count">
                  {chatHistory.length - 1} messages
                </div>
              </div>
            </div>

            <p className="text-center text-gray-700 font-medium mt-4">
              Get instant help with programming questions, code explanations, and tech insights
            </p>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-6">
          {/* Quick Suggestions */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FiZap className="text-primary" />
              <h2 className="section-title">Quick Suggestions</h2>
            </div>
            <div className="suggestions-grid">
              {suggestions.map((sugg, index) => (
                <button
                  key={index}
                  onClick={() => generateAnswer(null, sugg)}
                  className="suggestion-chip"
                  disabled={generatingAnswer || !isConnected}
                  title="Click to ask"
                >
                  {sugg}
                </button>
              ))}
            </div>
          </div>

          {/* Chat Container */}
          <div className="chat-container max-w-4xl mx-auto h-[500px] flex flex-col mb-6 relative">
            <div ref={chatContainerRef} className="chat-messages-container">
              {chatHistory.map((chat) => (
                <div
                  key={chat.id}
                  className={`chat-message ${chat.type}`}
                >
                  {chat.type === "ai" && (
                    <div className="avatar avatar-ai">
                      <RiRobot2Line size={18} />
                    </div>
                  )}

                  <div
                    className={`message-bubble ${chat.type} ${
                      chat.isError ? "error" : ""
                    } ${chat.isStreaming ? "streaming" : ""}`}
                  >
                    <div className="message-content">
                      <div className="markdown-content">
                        {chat.isStreaming ? (
                          <div className="streaming-content">
                            <ReactMarkdown>{streamedContent}</ReactMarkdown>
                            <span className="streaming-cursor">▋</span>
                          </div>
                        ) : (
                          <ReactMarkdown>{chat.content}</ReactMarkdown>
                        )}
                      </div>
                      <div className="message-footer">
                        <span className="timestamp">
                          {chat.timestamp || "Just now"}
                          {chat.isStreaming && " • Streaming..."}
                        </span>
                        <div className="message-actions">
                          <button
                            onClick={() => copyToClipboard(chat.content, chat.id)}
                            className="copy-btn"
                            title="Copy to clipboard"
                          >
                            {copiedMessageId === chat.id ? (
                              <>
                                <BsCheckCircle className="text-success" />
                                <span className="text-success">Copied!</span>
                              </>
                            ) : (
                              <>
                                <FiCopy />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {chat.type === "user" && (
                    <div className="avatar avatar-user">
                      <FiUser size={18} />
                    </div>
                  )}
                </div>
              ))}

              {generatingAnswer && !aiStreaming && (
                <div className="chat-message ai">
                  <div className="avatar avatar-ai">
                    <RiRobot2Line size={18} />
                  </div>
                  <div className="message-bubble ai loading">
                    <div className="typing-indicator">
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                      <div className="typing-dot"></div>
                    </div>
                    <span className="typing-text">Thinking...</span>
                  </div>
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </div>

            {/* Scroll to bottom button */}
            {(aiStreaming || generatingAnswer) && (
              <button
                onClick={handleScrollToBottom}
                className="scroll-to-bottom-btn"
                title="Scroll to bottom"
              >
                ↓
              </button>
            )}
          </div>

          {/* Input Area */}
          <form onSubmit={generateAnswer} className="input-container max-w-4xl mx-auto">
            <div className="input-wrapper">
              <div className="textarea-container">
                <textarea
                  className="input-textarea"
                  rows="3"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={
                    !isConnected
                      ? "⚠️ AI service temporarily unavailable. Please try again later."
                      : "Ask anything about coding, technology, or programming..."
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      generateAnswer(e);
                    }
                  }}
                  disabled={generatingAnswer || !isConnected}
                />
                <div className="textarea-footer">
                  <span className="instructions">
                    {!isConnected
                      ? "⚠️ AI service temporarily unavailable"
                      : "Press Enter to send • Shift+Enter for new line"}
                  </span>
                  <span className={`char-count ${question.length > 900 ? 'warning' : ''}`}>
                    {question.length}/1000
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={!question.trim() || generatingAnswer || !isConnected}
                className="send-button"
              >
                <FiSend size={18} />
                <span>{generatingAnswer ? "Processing..." : "Send"}</span>
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="footer">
            <p className="footer-text">
              <RiRobot2Line className="footer-icon" />
              Powered by V-EDITOR AI Assistant •{" "}
              <a
                href="https://veditor.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link"
              >
                veditor.in
              </a>
            </p>
            <p className="footer-disclaimer">
              This AI may produce inaccurate information. Always verify critical code.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Suggestion;